import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { dbOperations } from '../../utils/supabase'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  EyeIcon,
  EyeSlashIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'

const ProductsPage = () => {
  const navigate = useNavigate()
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [activeTab, setActiveTab] = useState('basic') // basic, details, sets
  
  // Dosya yükleme state'leri - ProductsPage için eklendi
  const [selectedFiles, setSelectedFiles] = useState([])
  const [uploadingImages, setUploadingImages] = useState(false)
  const [uploadedImages, setUploadedImages] = useState([])

  // Drag & Drop functions
  const [draggedIndex, setDraggedIndex] = useState(null)

  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null)
      return
    }

    const newImages = [...uploadedImages]
    const draggedImage = newImages[draggedIndex]
    
    // Remove dragged item
    newImages.splice(draggedIndex, 1)
    
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage)
    
    setUploadedImages(newImages)
    setDraggedIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  // Arrow movement functions
  const moveImageUp = (index) => {
    if (index === 0) return
    const newImages = [...uploadedImages]
    const temp = newImages[index]
    newImages[index] = newImages[index - 1]
    newImages[index - 1] = temp
    setUploadedImages(newImages)
  }

  const moveImageDown = (index) => {
    if (index === uploadedImages.length - 1) return
    const newImages = [...uploadedImages]
    const temp = newImages[index]
    newImages[index] = newImages[index + 1]
    newImages[index + 1] = temp
    setUploadedImages(newImages)
  }

  // Dosya seçme
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    setSelectedFiles(prev => [...prev, ...files])
  }

  // Seçili dosyayı kaldırma
  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  // Yüklenmiş resmi kaldırma
  const removeUploadedImage = async (index) => {
    const imageToRemove = uploadedImages[index]
    try {
      // Supabase'den dosyayı sil
      if (imageToRemove.fileName) {
        await dbOperations.storage.deleteFile(imageToRemove.fileName)
      }
      // State'den kaldır
      setUploadedImages(prev => prev.filter((_, i) => i !== index))
    } catch (error) {
      console.error('Error deleting image:', error)
    }
  }

  // Resimleri yükleme
  const uploadImages = async () => {
    if (selectedFiles.length === 0) return

    setUploadingImages(true)
    try {
      const uploadPromises = selectedFiles.map(file => 
        dbOperations.storage.uploadImage(file)
      )
      
      const results = await Promise.all(uploadPromises)
      
      const successfulUploads = results
        .filter(result => !result.error)
        .map(result => ({
          url: result.url,
          fileName: result.fileName
        }))

      const newUploadedImages = [...uploadedImages, ...successfulUploads]
      setUploadedImages(newUploadedImages)
      setSelectedFiles([])

      // Ana resim URL'sini güncelle (ilk yüklenen resim)
      if (successfulUploads.length > 0 && !formData.image_url) {
        setFormData(prev => ({
          ...prev,
          image_url: successfulUploads[0].url
        }))
      }
    } catch (error) {
      console.error('Error uploading images:', error)
    } finally {
      setUploadingImages(false)
    }
  }

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image_url: '',
    ai_image_url: '',
    model_3d_url: '',
    stock: '',
    is_active: true,
    is_featured: false,
    // Detay alanları
    dimensions: '',
    material: '',
    color: '',
    weight: '',
    brand: '',
    model: '',
    features: '',
    care_instructions: '',
    warranty_months: '24',
    tags: '',
    sku: '',
    barcode: ''
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [productsResponse, categoriesResponse] = await Promise.all([
        dbOperations.products.getAll(),
        dbOperations.categories.getAll()
      ])
      
      // Null/undefined product'ları filtrele (admin panelinde ham fiyatları göster)
      const validProducts = (productsResponse.data || []).filter(product => 
        product && product.id && product.name
      )
      
      setProducts(validProducts)
      setCategories(categoriesResponse.data || [])
    } catch (error) {
      console.error('Error loading data:', error)
      alert('Veriler yüklenirken hata oluştu. Lütfen sayfayı yenileyin.')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      // Önce kalan dosyaları yükle
      let finalUploadedImages = [...uploadedImages]
      if (selectedFiles.length > 0) {
        setUploadingImages(true)
        try {
          const uploadPromises = selectedFiles.map(file => 
            dbOperations.storage.uploadImage(file)
          )
          
          const results = await Promise.all(uploadPromises)
          
          const successfulUploads = results
            .filter(result => !result.error)
            .map(result => ({
              url: result.url,
              fileName: result.fileName
            }))

          finalUploadedImages = [...finalUploadedImages, ...successfulUploads]
        } catch (error) {
          console.error('Error uploading images:', error)
        } finally {
          setUploadingImages(false)
        }
      }

      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        weight: formData.weight ? parseFloat(formData.weight) : null,
        warranty_months: parseInt(formData.warranty_months),
        // Çoklu görsel desteği - images array olarak kaydet
        images: finalUploadedImages.map((img, index) => ({
          url: img.url,
          fileName: img.fileName,
          is_main: index === 0, // İlk görsel ana görsel
          sort_order: index
        }))
      }

      // Ana resim URL'si yoksa ve yüklenmiş resim varsa, ilk yüklenmiş resmi kullan
      if (!productData.image_url && finalUploadedImages.length > 0) {
        productData.image_url = finalUploadedImages[0].url
      }

      if (editingProduct) {
        const { data } = await dbOperations.products.update(editingProduct.id, productData)
        setProducts(products.map(p => p.id === editingProduct.id ? data : p))
      } else {
        const { data } = await dbOperations.products.create(productData)
        setProducts([data, ...products])
      }

      resetForm()
      setShowModal(false)
      setActiveTab('basic')
    } catch (error) {
      console.error('Error saving product:', error)
      alert('Ürün kaydedilirken hata oluştu. Lütfen tekrar deneyin.')
    }
  }

  const handleEdit = (product) => {
    // Yeni edit sayfasına yönlendir
    navigate(`/admin/products/edit/${product.id}`)
  }

  const handleDelete = async (productId) => {
    if (window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      try {
        await dbOperations.products.delete(productId)
        setProducts(products.filter(p => p.id !== productId))
      } catch (error) {
        console.error('Error deleting product:', error)
      }
    }
  }

  const toggleActive = async (product) => {
    try {
      const { data } = await dbOperations.products.update(product.id, {
        is_active: !product.is_active
      })
      setProducts(products.map(p => p.id === product.id ? data : p))
    } catch (error) {
      console.error('Error updating product status:', error)
    }
  }

  const toggleFeatured = async (product) => {
    try {
      const { data, error } = await dbOperations.products.update(product.id, {
        is_featured: !product.is_featured
      })
      
      if (error) {
        console.error('Database error:', error)
        alert('Anasayfa durumu güncellenirken hata oluştu: ' + error.message)
        return
      }
      
      if (data) {
        setProducts(products.map(p => p.id === product.id ? data : p))
      }
    } catch (error) {
      console.error('Error updating featured status:', error)
      alert('Anasayfa durumu güncellenirken hata oluştu. Lütfen sayfayı yenileyin.')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      image_url: '',
      ai_image_url: '',
      model_3d_url: '',
      stock: '',
      is_active: true,
      is_featured: false,
      // Detay alanları
      dimensions: '',
      material: '',
      color: '',
      weight: '',
      brand: '',
      model: '',
      features: '',
      care_instructions: '',
      warranty_months: '24',
      tags: '',
      sku: '',
      barcode: ''
    })
    setEditingProduct(null)
    setUploadedImages([])
    setSelectedFiles([])
  }

  const filteredProducts = products.filter(product => {
    // Null check için güvenlik
    if (!product || !product.name) return false
    
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = !filterCategory || product.category === filterCategory
    return matchesSearch && matchesCategory
  })

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Başlık ve Ekle Butonu */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold text-gray-900">Ürünler</h1>
          <p className="mt-2 text-sm text-gray-700">
            Tüm ürünleri görüntüleyin, düzenleyin ve yeni ürün ekleyin.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-3 flex">
          <button
            type="button"
            onClick={() => navigate('/admin/products/add')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Ürün Ekle
          </button>
          <button
            type="button"
            onClick={() => {
              resetForm()
              setShowModal(true)
              setActiveTab('basic')
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Hızlı Ürün Ekle
          </button>
        </div>
      </div>

      {/* Filtreler */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4 sm:space-y-0 sm:flex sm:gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Ürün adı veya açıklama ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Tüm Kategoriler</option>
            {categories.map(category => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Ürün Tablosu */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Ürün bulunamadı</h3>
            <p className="mt-1 text-sm text-gray-500">
              Arama kriterlerinize uygun ürün bulunmuyor.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filteredProducts.map((product) => (
              <li key={product.id}>
                <div className="px-4 py-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-16 w-16">
                      {product.image_url ? (
                        <img
                          className="h-16 w-16 rounded-lg object-cover"
                          src={product.image_url}
                          alt={product.name}
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-lg bg-gray-200 flex items-center justify-center">
                          <PhotoIcon className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">{product.name}</p>
                        {!product.is_active && (
                          <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            Pasif
                          </span>
                        )}
                        {product.is_featured === true && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            <StarIconSolid className="w-3 h-3 mr-1" />
                            Anasayfa
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{product.description}</p>
                      <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                        <span>{product.category}</span>
                        <span>•</span>
                        <span className="font-medium text-gray-900">{formatCurrency(product.price)}</span>
                        <span>•</span>
                        <span className={product.stock < 10 ? 'text-red-600 font-medium' : ''}>
                          {product.stock} adet
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleFeatured(product)}
                      className={`p-2 rounded-full ${
                        product.is_featured === true
                          ? 'text-yellow-600 hover:bg-yellow-50' 
                          : 'text-gray-400 hover:bg-gray-50'
                      }`}
                      title={product.is_featured === true ? 'Anasayfadan Çıkar' : 'Anasayfaya Ekle'}
                    >
                      {product.is_featured === true ? (
                        <StarIconSolid className="h-5 w-5" />
                      ) : (
                        <StarIcon className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => toggleActive(product)}
                      className={`p-2 rounded-full ${
                        product.is_active 
                          ? 'text-green-600 hover:bg-green-50' 
                          : 'text-gray-400 hover:bg-gray-50'
                      }`}
                      title={product.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                    >
                      {product.is_active ? (
                        <EyeIcon className="h-5 w-5" />
                      ) : (
                        <EyeSlashIcon className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(product)}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full"
                      title="Düzenle"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                      title="Sil"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingProduct ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
              </h3>
              
              {/* Sekme Başlıkları */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTab('basic')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'basic'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Temel Bilgiler
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('details')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'details'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Detay Bilgileri
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('advanced')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'advanced'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Gelişmiş
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('sets')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'sets'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Takım İçeriği
                  </button>
                </nav>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Temel Bilgiler Sekmesi */}
                {activeTab === 'basic' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Ürün Adı</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Kategori</label>
                      <select
                        required
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Kategori Seçin</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.name}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fiyat (₺)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Stok</label>
                      <input
                        type="number"
                        min="0"
                        required
                        value={formData.stock}
                        onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">SKU (Stok Kodu)</label>
                      <input
                        type="text"
                        value={formData.sku}
                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    {/* Resim Yükleme */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ürün Görselleri {uploadingImages && <span className="text-indigo-600">(Yükleniyor...)</span>}
                      </label>
                      
                      {/* File Upload */}
                      <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                        <div className="space-y-1 text-center">
                          <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <div className="flex text-sm text-gray-600">
                            <label
                              htmlFor="file-upload-modal"
                              className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                            >
                              <span>Dosya seçin</span>
                              <input
                                id="file-upload-modal"
                                name="file-upload-modal"
                                type="file"
                                className="sr-only"
                                multiple
                                accept="image/*"
                                onChange={handleFileSelect}
                                disabled={uploadingImages}
                              />
                            </label>
                            <p className="pl-1">veya sürükle bırak</p>
                          </div>
                          <p className="text-xs text-gray-500">PNG, JPG, GIF 10MB'a kadar</p>
                        </div>
                      </div>

                      {/* Seçili Dosyalar */}
                      {selectedFiles.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Seçili Dosyalar</h4>
                          <div className="flex gap-2 flex-wrap">
                            {selectedFiles.map((file, index) => (
                              <div key={index} className="relative bg-gray-100 p-2 rounded border w-20">
                                <button
                                  type="button"
                                  onClick={() => removeSelectedFile(index)}
                                  className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                                >
                                  ×
                                </button>
                                <p className="text-xs text-gray-600 mt-1 truncate">{file.name}</p>
                              </div>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={uploadImages}
                            disabled={uploadingImages}
                            className="mt-2 px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:bg-gray-400"
                          >
                            {uploadingImages ? 'Yükleniyor...' : 'Yükle'}
                          </button>
                        </div>
                      )}

                      {/* Yüklenmiş Görseller */}
                      {uploadedImages.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">
                            📸 Yüklenmiş Görseller ({uploadedImages.length})
                            <span className="text-xs text-gray-500 ml-1">
                              (Sürükleyerek sıralayabilirsiniz)
                            </span>
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {uploadedImages.map((image, index) => (
                              <div 
                                key={index} 
                                className={`relative group cursor-move transition-all duration-200 ${
                                  draggedIndex === index ? 'opacity-50 scale-95' : 'hover:scale-105'
                                }`}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={handleDragOver}
                                onDragEnter={handleDragEnter}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                              >
                                <div className="aspect-square relative overflow-hidden rounded-lg border-2 border-gray-200 hover:border-indigo-300">
                                  <img
                                    src={image.url}
                                    alt={`Ürün görseli ${index + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                  
                                  {/* Ana Görsel Badge */}
                                  {index === 0 ? (
                                    <div className="absolute top-1 left-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs px-1 py-0.5 rounded-full font-medium shadow-md">
                                      ⭐ Ana
                                    </div>
                                  ) : (
                                    <div className="absolute top-1 left-1 bg-indigo-600 text-white text-xs px-1 py-0.5 rounded-full font-medium">
                                      {index + 1}
                                    </div>
                                  )}

                                  {/* Controls Overlay */}
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                                    <div className="flex space-x-1">
                                      {index > 0 && (
                                        <button
                                          type="button"
                                          onClick={() => moveImageUp(index)}
                                          className="p-1 bg-white text-gray-700 rounded-full hover:bg-gray-100 shadow-md transition-all text-xs"
                                          title="Öne al"
                                        >
                                          ↑
                                        </button>
                                      )}
                                      {index < uploadedImages.length - 1 && (
                                        <button
                                          type="button"
                                          onClick={() => moveImageDown(index)}
                                          className="p-1 bg-white text-gray-700 rounded-full hover:bg-gray-100 shadow-md transition-all text-xs"
                                          title="Arkaya al"
                                        >
                                          ↓
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => removeUploadedImage(index)}
                                        className="p-1 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-md transition-all text-xs"
                                        title="Sil"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Manuel URL Girişi (Opsiyonel) */}
                      <div className="border-t pt-4 mt-4">
                        <label className="block text-sm font-medium text-gray-700">Manuel Resim URL (Opsiyonel)</label>
                        <input
                          type="url"
                          value={formData.image_url}
                          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                          placeholder="https://example.com/resim.jpg"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>


                    
                    <div className="md:col-span-2 space-y-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-900">
                          Ürün aktif
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.is_featured}
                          onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                          className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-900 flex items-center">
                          <StarIconSolid className="w-4 h-4 mr-1 text-yellow-500" />
                          Anasayfada göster
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Detay Bilgileri Sekmesi */}
                {activeTab === 'details' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ölçüler</label>
                      <input
                        type="text"
                        placeholder="örn: 200x80x85 cm"
                        value={formData.dimensions}
                        onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ağırlık (kg)</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.weight}
                        onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Malzeme</label>
                      <input
                        type="text"
                        placeholder="örn: Gerçek deri, metal ayak"
                        value={formData.material}
                        onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Renk</label>
                      <input
                        type="text"
                        placeholder="örn: Siyah, Kahverengi"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Marka</label>
                      <input
                        type="text"
                        value={formData.brand}
                        onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Model</label>
                      <input
                        type="text"
                        value={formData.model}
                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Garanti (ay)</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.warranty_months}
                        onChange={(e) => setFormData({ ...formData, warranty_months: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Barkod</label>
                      <input
                        type="text"
                        value={formData.barcode}
                        onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Özellikler</label>
                      <textarea
                        placeholder="Ürünün öne çıkan özelliklerini yazın..."
                        value={formData.features}
                        onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                        rows={3}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Bakım Talimatları</label>
                      <textarea
                        placeholder="Ürünün bakımı ve temizliği hakkında bilgiler..."
                        value={formData.care_instructions}
                        onChange={(e) => setFormData({ ...formData, care_instructions: e.target.value })}
                        rows={2}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Etiketler</label>
                      <input
                        type="text"
                        placeholder="Virgülle ayırarak yazın (örn: modern,deri,lüks)"
                        value={formData.tags}
                        onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                )}

                {/* Gelişmiş Sekmesi */}
                {activeTab === 'advanced' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-lg font-medium text-gray-900 mb-2">🤖 AI Görsel URL</label>
                      <p className="text-sm text-gray-600 mb-4">fal.ai için kullanılacak "evimde gör" görseli</p>
                      <input
                        type="url"
                        value={formData.ai_image_url}
                        onChange={(e) => setFormData({ ...formData, ai_image_url: e.target.value })}
                        placeholder="https://example.com/ai-image.jpg"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                      />
                      {formData.ai_image_url && (
                        <div className="mt-2">
                          <img 
                            src={formData.ai_image_url} 
                            alt="AI URL Preview" 
                            className="h-24 w-24 object-cover rounded-md border-2 border-purple-200"
                            onError={(e) => {
                              e.target.style.display = 'none'
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-lg font-medium text-gray-900 mb-2">🎲 3D Model URL</label>
                      <p className="text-sm text-gray-600 mb-4">Ürünün 3D modeli (.glb formatında)</p>
                      <input
                        type="url"
                        value={formData.model_3d_url}
                        onChange={(e) => setFormData({ ...formData, model_3d_url: e.target.value })}
                        placeholder="https://example.com/model.glb"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500"
                      />
                      {formData.model_3d_url && (
                        <div className="mt-2 p-3 bg-green-50 rounded border-2 border-green-200">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center mr-3">
                              <span className="text-green-600 text-xs font-bold">3D</span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-green-900">3D Model</p>
                              <p className="text-xs text-green-600 truncate max-w-xs">
                                {formData.model_3d_url.split('/').pop()}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">ℹ️ Bilgi</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>• <strong>AI Görseli:</strong> Kullanıcılar "evimde gör" dediğinde fal.ai'ye gönderilir</li>
                          <li>• <strong>3D Model:</strong> Ürünün interaktif 3D görünümü için kullanılır</li>
                          <li>• Bu alanlar opsiyoneldir, boş bırakılabilir</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Takım İçeriği Sekmesi */}
                {activeTab === 'sets' && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">
                      Takım özelliği yakında eklenecek!
                    </p>
                    <p className="text-sm text-gray-400">
                      Bu özellik ile ürünleri bir araya getirerek takımlar oluşturabileceksiniz.
                    </p>
                  </div>
                )}
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false)
                      setActiveTab('basic')
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    {editingProduct ? 'Güncelle' : 'Ekle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductsPage